import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getProspectorFunil,
  getProspectorKpis,
  getProspectorLeads,
  getProspectorLote,
  getProspectorMatches,
  getProspectorThreads,
  postProspectorMatch,
  postProspectorRespostaManual,
} from '@/lib/api';

// ── tipos (espelham admin-api/routers/prospector.py) ─────────────────────────

interface CampanhaAnalytics {
  leads: number | null;
  contacted: number | null;
  sent: number | null;
  opens: number | null;
  opens_unique: number | null;
  replies: number | null;
  bounces: number | null;
  opt_outs: number | null;
  completed: number | null;
  clicks: number | null;
}

interface Campanha {
  id: string | null;
  name: string | null;
  status: string;
  analytics: CampanhaAnalytics | null;
}

interface SignupDetalhe {
  user_id: string;
  user_email: string;
  created_at: string | null;
  lead_id: number;
  company: string | null;
  match: 'email' | 'dominio';
}

interface KpisData {
  disponivel: boolean;
  erro?: string;
  campanhas: Campanha[];
  instantly_configurada: boolean;
  db: {
    total_sent: number;
    total_replied: number;
    leads_contatados: number;
    leads_respondidos: number;
    reply_rate: number | null;
    classificacoes: Record<string, number>;
    opt_outs: number;
    por_dia: { dia: string; enviados: number; replies: number }[];
  };
  conversao: {
    signups: number;
    signups_detalhe: SignupDetalhe[];
    casos: number;
    pagantes: number | null;
  };
}

interface LeadRow {
  lead_id: number;
  company: string | null;
  contact: string | null;
  email: string | null;
  city: string | null;
  uf: string | null;
  sent_at: string | null;
  num_emails: number;
  replied_at: string | null;
  reply_classification: string | null;
  opted_out: boolean;
  opens: number | null;
  clicks: number | null;
  signup_user_id: string | null;
  tem_caso: boolean;
}

interface ThreadMsg {
  tipo: 'enviado' | 'resposta';
  sequence_step?: number;
  subject?: string;
  sent_at?: string | null;
  body_html?: string;
  replied_at?: string | null;
  texto?: string | null;
  classificacao?: string | null;
}

interface Thread {
  lead_id: number;
  company: string | null;
  contact: string | null;
  email: string | null;
  last_reply: string | null;
  classificacao: string | null;
  mensagens: ThreadMsg[];
}

// Funil a–n (GET /prospector/funil) — fase 1.
interface FunilLinha {
  campanha: string;
  campanha_nome: string;
  segmento?: string;
  segmentos: string[];
  a_contatos_obtidos: number;
  a_contatados: number;
  b_enviados: number;
  b_cold: number;
  b_followups: number;
  c_entregues: number | null;
  d_aberturas_unicas: number | null;
  d_rastreado: boolean;
  e_cliques: number | null;
  f_respostas: number;
  f_leads_responderam: number;
  f_qualificadas: number;
  g_nossa_resposta: number | null;
  g_manual: number | null;
  g_sem_resposta: number | null;
  h_lead_voltou: number;
  i_cadastros: number;
  // [ASSISTIDO 22/10] contas que NOS abrimos e o cliente ainda nao acessou:
  // ficam FORA de i_cadastros (nao sao aquisicao) ate o 1o login dele.
  i_assistidos: number;
  j_casos: number;
  k_downloads: number;
  l_tres_creditos: number;
  m_compras: number;
  m_receita_brl: number;
  n_recompras: number;
  resp_por_100: number | null;
  pendentes: {
    draft_pending: number;
    queued: number;
    rejected: number;
    misdirected: number;
  };
}

interface SemReplica {
  lead_id: number;
  segmento: string;
  campanha: string;
  company: string | null;
  contact: string | null;
  email: string | null;
  respondeu_em: string | null;
  dias_sem_replica: number | null;
  classificacao: string | null;
}

interface FunilData {
  disponivel: boolean;
  erro?: string;
  periodo: { dias: number; ini: string | null; fim: string };
  granularidade: 'campanha' | 'segmento';
  linhas: FunilLinha[];
  totais: FunilLinha;
  sem_replica: SemReplica[];
  fontes: {
    engajamento: boolean;
    nossa_resposta: boolean;
    lead_user_match: boolean;
    mapa_campanhas: string;
  };
  campanhas_conhecidas: {
    chave: string;
    nome: string;
    caixa: string | null;
    rastreia_abertura: boolean;
  }[];
  segmentos_conhecidos: string[];
  avisos: string[];
}

interface LoteStatus {
  n: number;
  mais_antigo: string | null;
  idade_dias: number | null;
}

interface LoteData {
  disponivel: boolean;
  erro?: string;
  por_status: Record<string, LoteStatus>;
  lote_do_dia: {
    arquivo: string;
    criado_em: string | null;
    origem: string | null;
    tamanho: number;
    ids: number[];
    ainda_em_draft: number;
    go_dado: boolean;
  };
  sidecar: {
    data: string | null;
    go_enviado_em: string | null;
    lembrete_enviado_em: string | null;
    estoque_dias: number | null;
  };
  verificador: null | Record<string, unknown>;
  avisos: string[];
}

interface MatchRow {
  id: number;
  lead_id: number;
  user_id: number;
  org_id: number | null;
  match_type: string;
  status: string;
  segmento: string | null;
  campanha: string;
  company: string | null;
  lead_email: string | null;
  user_email: string | null;
  first_touch_at: string | null;
  user_created_at: string | null;
  confirmed_by: string | null;
  // [ASSISTIDO 22/10] conta aberta por nós para o prospect; só vira usuário
  // adquirido quando o cliente entra sozinho (assistido_ativado_em).
  assistido: boolean;
  assistido_por: string | null;
  acesso_enviado_em: string | null;
  assistido_ativado_em: string | null;
  estado_aquisicao: string;
}

interface MatchesData {
  disponivel: boolean;
  erro?: string;
  matches: MatchRow[];
}

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtData = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString('pt-BR') : '—';
const fmtDataHora = (s: string | null | undefined) =>
  s
    ? new Date(s).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';
const num = (v: number | null | undefined) => (v != null ? String(v) : '—');
const fmtBRL = (v: number | null | undefined) =>
  v == null
    ? '—'
    : v.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      });

// null ≠ 0. Null = "não medimos" e sai como "—" cinza com o porquê no title;
// 0 = "medimos e deu zero". Misturar os dois é a mentira mais cara da aba.
function Cel({
  v,
  porque,
  forte,
}: {
  v: number | null | undefined;
  porque?: string;
  forte?: boolean;
}) {
  if (v == null)
    return (
      <span
        className="text-slate-300 cursor-help"
        title={porque ?? 'não instrumentado — ver avisos no topo'}
      >
        —
      </span>
    );
  return (
    <span className={forte ? 'font-semibold text-slate-800' : 'text-slate-600'}>
      {new Intl.NumberFormat('pt-BR').format(v)}
    </span>
  );
}

// Sanitização básica do NOSSO body_html (defesa em profundidade — o HTML é
// gerado por nós, mas removemos scripts/handlers antes do dangerouslySet).
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

const CLASSIF_LABEL: Record<string, string> = {
  interested: '🔥 Interessado',
  question: 'Pergunta',
  other: 'Outro',
  opt_out: 'Opt-out',
  unsubscribe: 'Opt-out',
  not_interested: 'Não interessado',
  out_of_office: 'Fora do escritório',
};

function classifBadgeClass(c: string | null | undefined): string {
  switch (c) {
    case 'interested':
      return 'bg-emerald-50 text-emerald-600';
    case 'question':
      return 'bg-sky-50 text-sky-600';
    case 'opt_out':
    case 'unsubscribe':
    case 'not_interested':
      return 'bg-rose-50 text-rose-600';
    default:
      return 'bg-slate-100 text-slate-500';
  }
}

function ClassifBadge({ c }: { c: string | null | undefined }) {
  if (!c) return <span className="text-slate-300">—</span>;
  return (
    <span
      className={`text-xs rounded-full px-2 py-0.5 whitespace-nowrap ${classifBadgeClass(c)}`}
    >
      {CLASSIF_LABEL[c] ?? c}
    </span>
  );
}

function Card({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${accent ?? 'text-slate-800'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

// ── página ───────────────────────────────────────────────────────────────────

const PERIODOS: { label: string; dias: number }[] = [
  { label: '7 d', dias: 7 },
  { label: '28 d', dias: 28 },
  { label: '90 d', dias: 90 },
  { label: 'tudo', dias: 0 },
];

export default function ProspectorPage() {
  const [kpis, setKpis] = useState<KpisData | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [funil, setFunil] = useState<FunilData | null>(null);
  const [lote, setLote] = useState<LoteData | null>(null);
  const [matches, setMatches] = useState<MatchesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [abertas, setAbertas] = useState<Set<number>>(new Set());

  // filtros (substituem o date picker global, inerte nesta aba)
  const [dias, setDias] = useState(28);
  const [segmento, setSegmento] = useState('');
  const [campanha, setCampanha] = useState('');
  const [granularidade, setGranularidade] =
    useState<'campanha' | 'segmento'>('campanha');

  // modal "registrar resposta manual"
  const [modalLead, setModalLead] = useState<SemReplica | null>(null);
  const [mEnviadoEm, setMEnviadoEm] = useState('');
  const [mAssunto, setMAssunto] = useState('');
  const [mTrecho, setMTrecho] = useState('');
  const [mSalvando, setMSalvando] = useState(false);
  const [mErro, setMErro] = useState<string | null>(null);

  const carregarFunil = useCallback(() => {
    return getProspectorFunil({ dias, segmento, campanha, granularidade })
      .then((r) => setFunil(r.data))
      .catch(() => setFunil(null));
  }, [dias, segmento, campanha, granularidade]);

  // 1ª carga: tudo. Os 3 endpoints antigos não reagem a segmento/campanha.
  useEffect(() => {
    setLoading(true);
    Promise.all([
      getProspectorKpis().then((r) => setKpis(r.data)),
      getProspectorLeads(dias).then((r) =>
        setLeads(r.data?.disponivel ? r.data.leads : []),
      ),
      getProspectorThreads(dias).then((r) =>
        setThreads(r.data?.disponivel ? r.data.threads : []),
      ),
      getProspectorLote()
        .then((r) => setLote(r.data))
        .catch(() => setLote(null)),
      getProspectorMatches()
        .then((r) => setMatches(r.data))
        .catch(() => setMatches(null)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(() => {
    carregarFunil();
  }, [carregarFunil]);

  const segmentosOpcoes = useMemo(
    () => funil?.segmentos_conhecidos ?? [],
    [funil],
  );
  const campanhasOpcoes = useMemo(
    () => funil?.campanhas_conhecidas ?? [],
    [funil],
  );

  async function salvarRespostaManual() {
    if (!modalLead) return;
    setMSalvando(true);
    setMErro(null);
    try {
      const iso = new Date(mEnviadoEm).toISOString();
      const r = await postProspectorRespostaManual({
        lead_id: modalLead.lead_id,
        enviado_em: iso,
        assunto: mAssunto || undefined,
        trecho: mTrecho || undefined,
      });
      if (!r.data?.ok) {
        setMErro(r.data?.erro ?? 'falha ao registrar');
        return;
      }
      setModalLead(null);
      setMAssunto('');
      setMTrecho('');
      await carregarFunil();
    } catch (e) {
      setMErro(String(e));
    } finally {
      setMSalvando(false);
    }
  }

  async function decidirMatch(id: number, status: 'confirmado' | 'descartado') {
    try {
      await postProspectorMatch(id, status);
      const r = await getProspectorMatches();
      setMatches(r.data);
      await carregarFunil();
    } catch {
      /* erro já aparece como estado inalterado */
    }
  }

  function toggleThread(leadId: number) {
    setAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }

  if (loading && !kpis)
    return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!kpis) return <div className="p-6 text-slate-400">Sem dados.</div>;
  if (!kpis.disponivel)
    return (
      <div className="p-6 text-rose-500">
        Indisponível: {kpis.erro} (tabelas lead/outreach_message migradas?)
      </div>
    );

  const db = kpis.db;
  const interessados = db.classificacoes['interested'] ?? 0;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Prospector</h1>
        <p className="text-sm text-slate-500">
          Cold outbound (Instantly) — envios, respostas, classificação e
          conversão lead → signup → caso.
          {!kpis.instantly_configurada && (
            <span className="ml-2 text-amber-600">
              INSTANTLY_API_KEY ausente — métricas da Instantly indisponíveis.
            </span>
          )}
        </p>
      </div>

      {/* Filtros — o date picker global não se aplica a esta aba
          (Layout.tsx: NO_DATE_FILTER_PATHS). O período daqui vai no ?dias=. */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase text-slate-400">Período</span>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {PERIODOS.map((p) => (
            <button
              key={p.dias}
              type="button"
              onClick={() => setDias(p.dias)}
              className={`px-3 py-1 text-xs ${
                dias === p.dias
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <span className="text-xs uppercase text-slate-400 ml-2">Segmento</span>
        <select
          value={segmento}
          onChange={(e) => setSegmento(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        >
          <option value="">todos</option>
          {segmentosOpcoes.map((sg) => (
            <option key={sg} value={sg}>
              {sg}
            </option>
          ))}
        </select>

        <span className="text-xs uppercase text-slate-400">Campanha</span>
        <select
          value={campanha}
          onChange={(e) => setCampanha(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        >
          <option value="">todas</option>
          {campanhasOpcoes.map((c) => (
            <option key={c.chave} value={c.chave}>
              {c.nome}
            </option>
          ))}
        </select>

        <div className="flex rounded-lg border border-slate-200 overflow-hidden ml-auto">
          {(['campanha', 'segmento'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularidade(g)}
              className={`px-3 py-1 text-xs ${
                granularidade === g
                  ? 'bg-slate-700 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              por {g}
            </button>
          ))}
        </div>
        {funil?.periodo?.ini && (
          <span className="text-[11px] text-slate-400 w-full">
            Janela: {funil.periodo.ini} → {funil.periodo.fim}
          </span>
        )}
      </div>

      {/* KPIs topo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card
          label="Enviados"
          value={String(db.total_sent)}
          accent="text-indigo-600"
          sub={`${db.leads_contatados} leads contatados`}
        />
        <Card
          label="Reply rate"
          value={db.reply_rate != null ? `${db.reply_rate}%` : '—'}
          accent="text-emerald-600"
          sub={`${db.leads_respondidos} leads responderam (${db.total_replied} respostas)`}
        />
        <Card
          label="Interessados 🔥"
          value={String(interessados)}
          accent="text-emerald-600"
          sub="classificação 'interested'"
        />
        <Card
          label="Opt-outs"
          value={String(db.opt_outs)}
          accent={db.opt_outs ? 'text-rose-600' : 'text-slate-800'}
          sub="leads com opted_out"
        />
        <Card
          label="Signups convertidos"
          value={String(kpis.conversao.signups)}
          accent="text-emerald-600"
          sub={`${kpis.conversao.casos} caso(s) subido(s)`}
        />
        {/* [22/10] conversao.pagantes era `None` hardcoded no router desde
            julho. Agora vem do funil (dedupe por org, mesma regra do
            funil_resumo.py). */}
        <Card
          label="Pagantes (orgs)"
          value={funil?.disponivel ? String(funil.totais.m_compras) : '—'}
          accent="text-emerald-600"
          sub={
            funil?.disponivel
              ? `${funil.totais.n_recompras} recompraram · janela de ${funil.periodo.dias || 'todo o'} ${funil.periodo.dias ? 'd' : 'período'}`
              : 'funil indisponível'
          }
        />
        <Card
          label="Receita atribuída"
          value={funil?.disponivel ? fmtBRL(funil.totais.m_receita_brl) : '—'}
          accent="text-emerald-600"
          sub="credit_transaction purchase, org deduplicada"
        />
      </div>

      {funil?.disponivel && funil.avisos.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-1">
          {funil.avisos.map((a, i) => (
            <div key={i} className="text-xs text-amber-800">
              ⚠ {a}
            </div>
          ))}
        </div>
      )}

      {/* Funil a–n — a entrega principal da fase 1 */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700 flex items-center gap-2">
          Funil a–n por {granularidade}
          <span className="text-xs font-normal text-slate-400">
            {funil?.disponivel
              ? `${funil.linhas.length} linha(s) · ${funil.periodo.dias ? `${funil.periodo.dias} d` : 'todo o período'}`
              : ''}
          </span>
          {funil?.disponivel && !funil.fontes.lead_user_match && (
            <span className="text-[10px] rounded-full px-2 py-0.5 bg-amber-50 text-amber-700">
              atribuição heurística
            </span>
          )}
        </div>
        {!funil ? (
          <div className="px-4 py-6 text-center text-slate-400 text-sm">
            Carregando funil…
          </div>
        ) : !funil.disponivel ? (
          <div className="px-4 py-6 text-center text-rose-500 text-sm">
            Indisponível: {funil.erro}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left sticky left-0 bg-slate-50">
                    {granularidade === 'campanha' ? 'Campanha' : 'Segmento'}
                  </th>
                <th className="px-2 py-2 text-right" title="a — leads criados na janela (estoque de topo)">a contatos</th>
                <th className="px-2 py-2 text-right" title="a — leads DISTINTOS com envio na janela">a contatados</th>
                <th className="px-2 py-2 text-right" title="b — outreach_message status='sent' na janela">b enviados</th>
                <th className="px-2 py-2 text-right text-slate-300" title="b — 1º toque (sequence_step = 0)">· cold</th>
                <th className="px-2 py-2 text-right text-slate-300" title="b — follow-ups (sequence_step > 0)">· follow</th>
                <th className="px-2 py-2 text-right" title="c — enviados menos bounces (requer outreach_engagement)">c entregues</th>
                <th className="px-2 py-2 text-right" title="d — leads com abertura (requer sync; '—' nas rotas sem tracking)">d aberturas</th>
                <th className="px-2 py-2 text-right" title="e — cliques (requer sync; '—' nas rotas sem tracking)">e cliques</th>
                <th className="px-2 py-2 text-right" title="f — mensagens com replied_at na janela">f respostas</th>
                <th className="px-2 py-2 text-right text-slate-300" title="f — leads distintos que responderam">· leads</th>
                <th className="px-2 py-2 text-right" title="f — classificadas interested ou question">f qualif.</th>
                <th className="px-2 py-2 text-right" title="g — leads a quem respondemos (outreach_nossa_resposta)">g réplica</th>
                <th className="px-2 py-2 text-right" title="g — réplicas registradas à mão do Zoho contato@">· manual</th>
                <th className="px-2 py-2 text-right" title="g — leads que responderam e ficaram sem réplica nossa">· sem réplica</th>
                <th className="px-2 py-2 text-right" title="h — o lead voltou a escrever depois da nossa réplica">h voltou</th>
                <th className="px-2 py-2 text-right" title="i — orgs cadastradas atribuídas (dedupe por org)">i cadastros</th>
                <th className="px-2 py-2 text-right" title="j — processos das orgs atribuídas">j casos</th>
                <th className="px-2 py-2 text-right" title="k — analytics_event download / memoria_download">k downloads</th>
                <th className="px-2 py-2 text-right" title="l — orgs com ≥3 créditos consumidos">l ≥3 créd.</th>
                <th className="px-2 py-2 text-right" title="m — orgs com ≥1 compra">m compras</th>
                <th className="px-2 py-2 text-right" title="m — receita Stripe/créditos, org deduplicada">m receita</th>
                <th className="px-2 py-2 text-right" title="n — orgs com ≥2 compras">n recompras</th>
                <th className="px-2 py-2 text-right" title="respostas por 100 e-mails enviados">resp./100</th>
                <th className="px-2 py-2 text-right" title="aguardando GO · na fila · rejeitados · auto-enviados">pendentes</th>
                </tr>
              </thead>
              <tbody>
                {funil.linhas.map((r) => (
                  <tr
                    key={r.campanha + (r.segmento ?? '')}
                    className="border-t border-slate-100 text-xs"
                  >
                    <td className="px-3 py-2 text-slate-700 sticky left-0 bg-white">
                      {granularidade === 'campanha' ? (
                        <>
                          {r.campanha_nome}
                          <div className="text-[10px] text-slate-400">
                            {r.segmentos.join(', ')}
                          </div>
                        </>
                      ) : (
                        <>
                          {r.segmento}
                          <div className="text-[10px] text-slate-400">
                            {r.campanha_nome}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right"><Cel v={r.a_contatos_obtidos} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.a_contatados} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.b_enviados} forte /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.b_cold} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.b_followups} /></td>
                    <td className="px-2 py-2 text-right">
                      <Cel v={r.c_entregues} porque="entrega exige outreach_engagement (sync de bounces da Instantly) — ainda não existe" />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Cel
                        v={r.d_aberturas_unicas}
                        porque={
                          r.d_rastreado
                            ? 'abertura exige o sync de opens da Instantly (outreach_engagement) — ainda não existe'
                            : 'open tracking DESLIGADO nesta campanha institucional (decisão de entregabilidade) — abertura é inconhecível, não zero'
                        }
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Cel
                        v={r.e_cliques}
                        porque={
                          r.d_rastreado
                            ? 'clique exige o sync da Instantly (outreach_engagement) — ainda não existe'
                            : 'link tracking DESLIGADO nesta campanha institucional'
                        }
                      />
                    </td>
                    <td className="px-2 py-2 text-right"><Cel v={r.f_respostas} forte /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.f_leads_responderam} /></td>
                    <td className="px-2 py-2 text-right">
                      <span className="font-semibold text-emerald-600">
                        {r.f_qualificadas}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Cel v={r.g_nossa_resposta} porque="exige outreach_nossa_resposta (unibox + registro manual do Zoho) — ainda não existe" />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Cel v={r.g_manual} porque="réplicas à mão do contato@ não são registradas enquanto a tabela não existir" />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className={r.g_sem_resposta ? 'font-semibold text-rose-600' : 'text-slate-400'}>
                        {num(r.g_sem_resposta)}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right"><Cel v={r.h_lead_voltou} /></td>
                    <td className="px-2 py-2 text-right">
                      <Cel v={r.i_cadastros} forte />
                      {r.i_assistidos > 0 && (
                        <div
                          className="text-[10px] font-normal text-amber-600"
                          title="Cadastro assistido: conta aberta por nós para o prospect. Só vira usuário adquirido quando o cliente entra sozinho, depois do e-mail de acesso."
                        >
                          assistidos: {r.i_assistidos}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right"><Cel v={r.j_casos} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.k_downloads} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.l_tres_creditos} /></td>
                    <td className="px-2 py-2 text-right"><Cel v={r.m_compras} forte /></td>
                    <td className="px-2 py-2 text-right text-slate-700">
                      {fmtBRL(r.m_receita_brl)}
                    </td>
                    <td className="px-2 py-2 text-right"><Cel v={r.n_recompras} /></td>
                    <td className="px-2 py-2 text-right text-indigo-600">
                      {r.resp_por_100 != null ? r.resp_por_100.toFixed(1) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right text-[10px] text-slate-500 whitespace-nowrap">
                      {r.pendentes.draft_pending}·{r.pendentes.queued}·
                      {r.pendentes.rejected}·{r.pendentes.misdirected}
                    </td>
                  </tr>
                ))}
                {funil.linhas.length === 0 && (
                  <tr>
                    <td colSpan={25} className="px-4 py-4 text-center text-slate-400">
                      Nenhum envio na janela com esses filtros.
                    </td>
                  </tr>
                )}
              </tbody>
              {funil.linhas.length > 0 && (
                <tfoot className="bg-slate-50 text-xs font-semibold text-slate-700">
                  <tr>
                    <td className="px-3 py-2 sticky left-0 bg-slate-50">TOTAL</td>
                    <td className="px-2 py-2 text-right">{funil.totais.a_contatos_obtidos}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.a_contatados}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.b_enviados}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.b_cold}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.b_followups}</td>
                    <td className="px-2 py-2 text-right">{num(funil.totais.c_entregues)}</td>
                    <td className="px-2 py-2 text-right">{num(funil.totais.d_aberturas_unicas)}</td>
                    <td className="px-2 py-2 text-right">{num(funil.totais.e_cliques)}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.f_respostas}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.f_leads_responderam}</td>
                    <td className="px-2 py-2 text-right text-emerald-600">{funil.totais.f_qualificadas}</td>
                    <td className="px-2 py-2 text-right">{num(funil.totais.g_nossa_resposta)}</td>
                    <td className="px-2 py-2 text-right">{num(funil.totais.g_manual)}</td>
                    <td className="px-2 py-2 text-right text-rose-600">{num(funil.totais.g_sem_resposta)}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.h_lead_voltou}</td>
                    <td className="px-2 py-2 text-right">
                      {funil.totais.i_cadastros}
                      {funil.totais.i_assistidos > 0 && (
                        <div className="text-[10px] font-normal text-amber-600">
                          assistidos: {funil.totais.i_assistidos}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">{funil.totais.j_casos}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.k_downloads}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.l_tres_creditos}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.m_compras}</td>
                    <td className="px-2 py-2 text-right">{fmtBRL(funil.totais.m_receita_brl)}</td>
                    <td className="px-2 py-2 text-right">{funil.totais.n_recompras}</td>
                    <td className="px-2 py-2 text-right text-indigo-600">
                      {funil.totais.resp_por_100 != null
                        ? funil.totais.resp_por_100.toFixed(1)
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right text-[10px]">
                      {funil.totais.pendentes.draft_pending}·
                      {funil.totais.pendentes.queued}·
                      {funil.totais.pendentes.rejected}·
                      {funil.totais.pendentes.misdirected}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            <div className="px-4 py-2 text-[10px] text-slate-400 border-t border-slate-100">
              pendentes = aguardando GO · na fila · rejeitados · auto-enviados
              (misdirected). Mapa segmento→campanha: {funil.fontes.mapa_campanhas}.
              "—" cinza = não medimos (passe o mouse p/ o motivo); 0 = medimos e deu zero.
            </div>
          </div>
        )}
      </div>

      {/* Campanhas (Instantly) */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Campanhas (Instantly) ({kpis.campanhas.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Campanha</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Opens</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">Replies</th>
                <th className="px-3 py-2 text-right">Bounces</th>
                <th className="px-3 py-2 text-right">Opt-outs</th>
              </tr>
            </thead>
            <tbody>
              {kpis.campanhas.map((c) => (
                <tr key={c.id ?? c.name} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">
                    {c.name ?? '—'}
                    {c.id && (
                      <div className="text-xs text-slate-400">{c.id}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        c.status === 'active'
                          ? 'bg-emerald-50 text-emerald-600'
                          : c.status === 'paused'
                            ? 'bg-amber-50 text-amber-600'
                            : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {num(c.analytics?.sent)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">
                    {num(c.analytics?.opens_unique)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">
                    {num(c.analytics?.clicks)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-600">
                    {num(c.analytics?.replies)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${
                      c.analytics?.bounces
                        ? 'text-rose-600 font-semibold'
                        : 'text-slate-400'
                    }`}
                  >
                    {num(c.analytics?.bounces)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${
                      c.analytics?.opt_outs ? 'text-amber-600' : 'text-slate-400'
                    }`}
                  >
                    {num(c.analytics?.opt_outs)}
                  </td>
                </tr>
              ))}
              {kpis.campanhas.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-center text-slate-400">
                    {kpis.instantly_configurada
                      ? 'Nenhuma campanha (ou API da Instantly indisponível).'
                      : 'Configure INSTANTLY_API_KEY no admin-api p/ ver as campanhas.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lote do dia + respostas sem réplica */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Lote do dia
          </h2>
          <p className="text-xs text-slate-400 mb-3">
            Só leitura. O GO continua sendo o <code>ok lote</code> do Telegram —
            o admin nunca dispara envio.
          </p>
          {!lote ? (
            <div className="text-sm text-slate-400">Carregando…</div>
          ) : !lote.disponivel ? (
            <div className="text-sm text-rose-500">
              Indisponível: {lote.erro}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(
                  [
                    ['draft_pending', 'aguardando GO', 'text-amber-600'],
                    ['queued', 'na fila', 'text-indigo-600'],
                    ['rejected', 'rejeitados', 'text-slate-600'],
                    ['misdirected', 'p/ nós mesmos', 'text-rose-600'],
                  ] as const
                ).map(([k, rotulo, cor]) => (
                  <div key={k} className="rounded-lg border border-slate-100 p-2">
                    <div className="text-[10px] uppercase text-slate-400">
                      {rotulo}
                    </div>
                    <div className={`text-2xl font-bold ${cor}`}>
                      {lote.por_status[k]?.n ?? 0}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {lote.por_status[k]?.idade_dias != null
                        ? `mais velho há ${lote.por_status[k]?.idade_dias} d`
                        : '—'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500">
                  Lote de{' '}
                  {lote.lote_do_dia.criado_em
                    ? fmtDataHora(lote.lote_do_dia.criado_em)
                    : '—'}{' '}
                  · {lote.lote_do_dia.tamanho} id(s)
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    lote.lote_do_dia.tamanho === 0
                      ? 'bg-slate-100 text-slate-500'
                      : lote.lote_do_dia.go_dado
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {lote.lote_do_dia.tamanho === 0
                    ? 'sem lote'
                    : lote.lote_do_dia.go_dado
                      ? 'GO dado'
                      : `GO pendente (${lote.lote_do_dia.ainda_em_draft} em draft)`}
                </span>
                {lote.sidecar.estoque_dias != null && (
                  <span className="text-slate-400">
                    estoque {lote.sidecar.estoque_dias} d
                  </span>
                )}
              </div>
              <div className="mt-2 text-[10px] text-slate-400">
                Verificador (MillionVerifier):{' '}
                <span className="text-slate-300">—</span> sem fonte no banco.
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Respostas sem réplica ({funil?.sem_replica?.length ?? 0})
          </h2>
          <p className="text-xs text-slate-400 mb-3">
            O lead respondeu e não há réplica nossa registrada depois disso.
            Interessado parado há mais de 3 dias sai em vermelho.
          </p>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {(funil?.sem_replica ?? []).map((r) => {
              const urgente =
                r.classificacao === 'interested' &&
                (r.dias_sem_replica ?? 0) > 3;
              return (
                <div
                  key={r.lead_id}
                  className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 ${
                    urgente ? 'bg-rose-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className="flex-1 min-w-0 truncate text-slate-700">
                    {r.company ?? r.contact ?? r.email ?? `Lead #${r.lead_id}`}
                    <span className="ml-1.5 text-slate-400">{r.campanha}</span>
                  </span>
                  <ClassifBadge c={r.classificacao} />
                  <span
                    className={`whitespace-nowrap ${urgente ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}
                  >
                    {r.dias_sem_replica != null ? `${r.dias_sem_replica} d` : '—'}
                  </span>
                  <span className="text-slate-400 whitespace-nowrap">
                    {fmtData(r.respondeu_em)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setModalLead(r);
                      setMErro(null);
                      setMEnviadoEm(
                        new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                          .toISOString()
                          .slice(0, 16),
                      );
                    }}
                    className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-100 whitespace-nowrap"
                  >
                    registrar resposta
                  </button>
                </div>
              );
            })}
            {(funil?.sem_replica ?? []).length === 0 && (
              <div className="text-sm text-slate-400">
                {funil?.disponivel
                  ? 'Nenhuma resposta sem réplica na janela.'
                  : 'Carregando…'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cadastros atribuídos (lead_user_match) */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Cadastros atribuídos ({matches?.matches?.length ?? 0})
        </div>
        {!matches ? (
          <div className="px-4 py-4 text-sm text-slate-400">Carregando…</div>
        ) : !matches.disponivel ? (
          <div className="px-4 py-4 text-sm text-slate-400">
            {matches.erro} — a atribuição exibida no funil vem do heurístico
            e-mail/domínio enquanto a tabela não existir.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Lead</th>
                  <th className="px-3 py-2 text-left">Usuário</th>
                  <th className="px-3 py-2 text-left">Campanha</th>
                  <th className="px-3 py-2 text-center">Tipo</th>
                  <th className="px-3 py-2 text-left">1º toque</th>
                  <th className="px-3 py-2 text-left">Cadastro</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {matches.matches.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 text-xs">
                    <td className="px-4 py-2 text-slate-700 max-w-[200px] truncate">
                      {m.company ?? `lead #${m.lead_id}`}
                      <div className="text-[10px] text-slate-400">
                        {m.lead_email}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">
                      {m.user_email ?? `user #${m.user_id}`}
                      <div className="text-[10px] text-slate-400">
                        org {m.org_id ?? '—'}
                      </div>
                      {m.assistido && !m.assistido_ativado_em && (
                        <div
                          className="text-[10px] font-medium text-amber-600"
                          title={`Cadastro assistido${m.assistido_por ? ` (por ${m.assistido_por})` : ''}: criado por nós, NÃO conta como adquirido até o cliente entrar sozinho.`}
                        >
                          ⚠️ {m.estado_aquisicao}
                        </div>
                      )}
                      {m.assistido && m.assistido_ativado_em && (
                        <div className="text-[10px] font-medium text-emerald-600">
                          ✅ assistido → adquirido
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{m.campanha}</td>
                    <td className="px-3 py-2 text-center text-slate-500">
                      {m.match_type}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {fmtData(m.first_touch_at)}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {fmtData(m.user_created_at)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          m.status === 'confirmado'
                            ? 'bg-emerald-50 text-emerald-600'
                            : m.status === 'descartado'
                              ? 'bg-rose-50 text-rose-600'
                              : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {m.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => decidirMatch(m.id, 'confirmado')}
                        disabled={m.status === 'confirmado'}
                        className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                      >
                        confirmar
                      </button>
                      <button
                        type="button"
                        onClick={() => decidirMatch(m.id, 'descartado')}
                        disabled={m.status === 'descartado'}
                        className="ml-1 rounded border border-rose-300 px-1.5 py-0.5 text-[10px] text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                      >
                        descartar
                      </button>
                    </td>
                  </tr>
                ))}
                {matches.matches.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-slate-400">
                      Nenhuma atribuição registrada ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Funil por lead */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Funil por lead ({leads?.length ?? 0} contatados)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Empresa</th>
                <th className="px-3 py-2 text-left">Contato</th>
                <th className="px-3 py-2 text-left">E-mail</th>
                <th className="px-3 py-2 text-left">Cidade</th>
                <th className="px-3 py-2 text-left">Enviado em</th>
                <th className="px-3 py-2 text-right">E-mails</th>
                <th className="px-3 py-2 text-right">Aberturas</th>
                <th className="px-3 py-2 text-right">Cliques</th>
                <th className="px-3 py-2 text-center">Resposta</th>
                <th className="px-3 py-2 text-center">Signup</th>
              </tr>
            </thead>
            <tbody>
              {(leads ?? []).map((l) => (
                <tr key={l.lead_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700 max-w-[220px] truncate">
                    {l.company ?? '—'}
                    {l.opted_out && (
                      <span className="ml-1.5 text-xs rounded-full px-1.5 py-0.5 bg-rose-50 text-rose-600">
                        opt-out
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[160px] truncate">
                    {l.contact ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs max-w-[200px] truncate">
                    {l.email ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                    {l.city ? `${l.city}${l.uf ? `/${l.uf}` : ''}` : (l.uf ?? '—')}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                    {fmtData(l.sent_at)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">
                    {l.num_emails}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${
                      l.opens ? 'text-indigo-600 font-semibold' : 'text-slate-400'
                    }`}
                  >
                    {num(l.opens)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${
                      l.clicks ? 'text-indigo-600 font-semibold' : 'text-slate-400'
                    }`}
                  >
                    {num(l.clicks)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {l.replied_at ? (
                      <div>
                        <ClassifBadge c={l.reply_classification ?? 'other'} />
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {fmtData(l.replied_at)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {l.signup_user_id ? (
                      <span className="text-xs rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-600 whitespace-nowrap">
                        ✓ signup{l.tem_caso ? ' + caso' : ''}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {(leads ?? []).length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-4 text-center text-slate-400">
                    Nenhum lead contatado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversas */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Conversas ({threads?.length ?? 0})
        </div>
        <div className="divide-y divide-slate-100">
          {(threads ?? []).map((t) => {
            const aberta = abertas.has(t.lead_id);
            return (
              <div key={t.lead_id}>
                <button
                  type="button"
                  onClick={() => toggleThread(t.lead_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                >
                  <span className="text-slate-400 text-xs w-3">
                    {aberta ? '▾' : '▸'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm text-slate-700 font-medium">
                      {t.company ?? t.email ?? `Lead #${t.lead_id}`}
                    </span>
                    {t.contact && (
                      <span className="ml-2 text-xs text-slate-500">
                        {t.contact}
                      </span>
                    )}
                    <span className="ml-2 text-xs text-slate-400">
                      {t.email}
                    </span>
                  </span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {fmtDataHora(t.last_reply)}
                  </span>
                  <ClassifBadge c={t.classificacao} />
                </button>
                {aberta && (
                  <div className="px-4 pb-4 space-y-3 bg-slate-50/60">
                    {t.mensagens.map((m, i) =>
                      m.tipo === 'enviado' ? (
                        <div key={i} className="flex justify-start">
                          <div className="max-w-[75%] rounded-xl rounded-tl-sm border border-slate-200 bg-slate-100 px-3.5 py-2.5">
                            <div className="text-xs text-slate-500 mb-1">
                              Enviado · step {m.sequence_step ?? '?'} ·{' '}
                              {fmtDataHora(m.sent_at)}
                            </div>
                            {m.subject && (
                              <div className="text-sm font-semibold text-slate-700 mb-1">
                                {m.subject}
                              </div>
                            )}
                            <div
                              className="text-sm text-slate-600 prose-sm max-w-none [&_a]:text-indigo-600"
                              // NOSSO body_html (gerado pelo prospector),
                              // ainda assim passa por sanitização básica.
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(m.body_html ?? ''),
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div key={i} className="flex justify-end">
                          <div className="max-w-[75%] rounded-xl rounded-tr-sm border border-emerald-200 bg-emerald-50 px-3.5 py-2.5">
                            <div className="text-xs text-emerald-700 mb-1 flex items-center gap-2">
                              <span>Resposta · {fmtDataHora(m.replied_at)}</span>
                              <ClassifBadge c={m.classificacao} />
                            </div>
                            {/* Reply de terceiro: SEMPRE texto puro. */}
                            <div className="text-sm text-slate-700 whitespace-pre-wrap">
                              {m.texto ?? '(sem conteúdo capturado)'}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    {t.mensagens.length === 0 && (
                      <div className="text-sm text-slate-400 pt-2">
                        Sem mensagens registradas.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {(threads ?? []).length === 0 && (
            <div className="px-4 py-4 text-center text-slate-400 text-sm">
              Nenhuma resposta ainda.
            </div>
          )}
        </div>
      </div>

      {/* Envios por dia (30d) + signups convertidos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Envios por dia (30d)
          </h2>
          <p className="text-xs text-slate-400 mb-3">
            Enviados × replies (atribuídos ao dia do envio).
          </p>
          <div className="space-y-1.5">
            {(() => {
              const dias = db.por_dia;
              const max = Math.max(1, ...dias.map((d) => d.enviados));
              return dias.map((d) => (
                <div key={d.dia} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-slate-500">{d.dia}</span>
                  <div className="flex-1 bg-slate-100 rounded h-3.5 overflow-hidden">
                    <div
                      className="bg-indigo-400 h-3.5"
                      style={{ width: `${(100 * d.enviados) / max}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-slate-700 font-semibold">
                    {d.enviados}
                  </span>
                  <span className="w-14 text-right text-emerald-600">
                    {d.replies ? `${d.replies} resp.` : ''}
                  </span>
                </div>
              ));
            })()}
            {db.por_dia.length === 0 && (
              <div className="text-sm text-slate-400">
                Nenhum envio nos últimos 30 dias.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">
            Signups convertidos
          </h2>
          <p className="text-xs text-slate-400 mb-3">
            u.email casando com lead contatado (exato ou por domínio), criado
            após o 1º envio.
          </p>
          <div className="space-y-2">
            {kpis.conversao.signups_detalhe.map((s) => (
              <div
                key={`${s.user_id}-${s.lead_id}`}
                className="flex items-center gap-2 text-sm"
              >
                <span className="flex-1 truncate text-slate-700" title={s.user_email}>
                  {s.user_email}
                </span>
                <span className="text-xs text-slate-400 truncate max-w-[160px]">
                  {s.company ?? `lead #${s.lead_id}`}
                </span>
                <span
                  className={`text-xs rounded-full px-2 py-0.5 ${
                    s.match === 'email'
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-sky-50 text-sky-600'
                  }`}
                >
                  {s.match}
                </span>
                <span className="text-xs text-slate-400 whitespace-nowrap">
                  {fmtData(s.created_at)}
                </span>
              </div>
            ))}
            {kpis.conversao.signups_detalhe.length === 0 && (
              <div className="text-sm text-slate-400">
                Nenhum signup atribuído ao outbound ainda.
              </div>
            )}
          </div>
        </div>
      </div>

      {modalLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-800">
              Registrar resposta manual
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Para a réplica que saiu à mão do Zoho <code>contato@</code> — ela
              não entra no banco por nenhum outro caminho. Idempotente: o mesmo
              lead + data/hora não duplica.
            </p>
            <div className="mt-3 text-xs text-slate-600">
              {modalLead.company ?? modalLead.email} · lead #{modalLead.lead_id}
            </div>
            <label className="block mt-3 text-xs text-slate-500">
              Enviada em
              <input
                type="datetime-local"
                value={mEnviadoEm}
                onChange={(e) => setMEnviadoEm(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block mt-2 text-xs text-slate-500">
              Assunto (opcional)
              <input
                type="text"
                value={mAssunto}
                onChange={(e) => setMAssunto(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block mt-2 text-xs text-slate-500">
              Trecho (opcional, ~300 chars)
              <textarea
                value={mTrecho}
                onChange={(e) => setMTrecho(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            {mErro && (
              <div className="mt-2 text-xs text-rose-600">{mErro}</div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalLead(null)}
                className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50"
              >
                cancelar
              </button>
              <button
                type="button"
                onClick={salvarRespostaManual}
                disabled={mSalvando || !mEnviadoEm}
                className="rounded bg-indigo-600 px-3 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {mSalvando ? 'salvando…' : 'registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
